import React, { useEffect } from "react"
import { useChildElements } from "../utils/use-child-elements"
import { createElement } from "./create-element"
import { ContentEditor } from "../editor/content-editor"
import { ImageGallery as Gallery } from "./gallery-element"
import { Feed } from "./feed-element"
import { LinksContainer as Links } from "./links-element"
import { IconContainer as Icons } from "./icons-element"

// Apply custom classes to certain elements.
function addClassNames(type, classNames = "") {
  switch (type) {
    case "Text":
      return classNames.concat(" formkit-text").trim()
    default:
      return classNames
  }
}

// Helps match a region's children to the correct component.
const components = {
  Text: createElement(ContentEditor),
  Gallery: createElement(Gallery),
  Feed: createElement(Feed),
  Links: createElement(Links),
  Icons: createElement(Icons)
}

// Assign an array of children to a parent, bypassing the region assignment.
const assignToParent = (children, parent_id) =>
  children.map(child => ({
    ...child,
    parent_element_id: parent_id,
    region: null
  }))

export function Region({ name, children: defaultChildren, className, group }) {
  const { children, addChild } = useChildElements("region", name)

  // Add this region's default children.
  useEffect(() => {
    if (children.length) return
    const defaults = React.Children.toArray(defaultChildren)

    defaults.map((child, index) => {
      // Add this element; its settings in `defaults` will be passed as props.
      const [{ element_id: parentId }] = addChild({
        type: child.type.componentType,
        order: index,
        ...child.props,
        ...child.props.defaults
      })

      // Add this element's children, if it has any. Feeds should not be given
      // any default children since they rely on user data.
      const collectionChildren = child.props.defaultChildren
      if (collectionChildren && child.type.componentType !== "Feed") {
        const childElements = assignToParent(collectionChildren, parentId)
        addChild(childElements, 0)
      }
    })
  }, [addChild, children.length, defaultChildren])

  // Render the children that were pushed into ElementContext above.
  return (
    <div className={className}>
      {children.map(element => {
        const Component = components[element.type]
        const classNames = addClassNames(element.type, element.className)
        return (
          <Component
            key={element.element_id}
            {...element}
            className={classNames}
            group={group}
          />
        )
      })}
    </div>
  )
}
